import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Typography, Box, Paper, Divider, Select, MenuItem, FormControl,
    InputLabel, TextField, Button, Stack, Grid, Switch, FormControlLabel,
    TextareaAutosize // Importamos TextareaAutosize
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppStore } from '../../../../../store/appStore.tsx';
import { useValue } from '../../../../../context/ContextProvider';
import { updateConfigAlarma } from '../../../../../actions/alarmaActions';

// --- Componente Reutilizable para Input de Tiempo (HH:MM:SS) ---
const TimeInput = ({ label, name, value = { h: 0, m: 0, s: 0 }, onChange }) => {
    const handleComponentChange = (component) => (event) => {
        let val = parseInt(event.target.value, 10);
        if (isNaN(val)) val = 0;
        const max = component === 'h' ? 23 : 59;
        val = Math.max(0, Math.min(val, max));
        const updatedTime = { ...value, [component]: val };
        const syntheticEvent = { target: { name, value: updatedTime, type: 'time_object' } };
        onChange(syntheticEvent);
    };

    return (
        <Box sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1, p: 2, position: 'relative' }}>
            <Typography variant="body2" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1 }}>
                {label}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ pt: 1 }}>
                <TextField label="HH" type="number" value={String(value.h || 0).padStart(2, '0')} onChange={handleComponentChange('h')} size="small" sx={{ width: 70 }} />
                <Typography variant="h6">:</Typography>
                <TextField label="MM" type="number" value={String(value.m || 0).padStart(2, '0')} onChange={handleComponentChange('m')} size="small" sx={{ width: 70 }} />
                <Typography variant="h6">:</Typography>
                <TextField label="SS" type="number" value={String(value.s || 0).padStart(2, '0')} onChange={handleComponentChange('s')} size="small" sx={{ width: 70 }} />
            </Stack>
        </Box>
    );
};

// --- Componente Reutilizable para Input de Tiempo (HH:MM) ---
const TimeInputHM = ({ label, name, value = { h: 0, m: 0 }, onChange }) => {
     const handleComponentChange = (component) => (event) => {
        let val = parseInt(event.target.value, 10);
        if (isNaN(val)) val = 0;
        const max = component === 'h' ? 23 : 59;
        val = Math.max(0, Math.min(val, max));
        const updatedTime = { ...value, [component]: val };
        const syntheticEvent = { target: { name, value: updatedTime, type: 'time_object' } };
        onChange(syntheticEvent);
    };
    return (
        <Box sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1, p: 2, position: 'relative' }}>
             <Typography variant="body2" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1 }}>
                {label}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ pt: 1 }}>
                <TextField label="HH" type="number" value={String(value.h || 0).padStart(2, '0')} onChange={handleComponentChange('h')} size="small" sx={{ width: 70 }} />
                <Typography variant="h6">:</Typography>
                <TextField label="MM" type="number" value={String(value.m || 0).padStart(2, '0')} onChange={handleComponentChange('m')} size="small" sx={{ width: 70 }} />
            </Stack>
        </Box>
    );
};


// --- Generador del estado inicial para el padre ---
export const generarEstadoInicialAc = () => {
    return Array.from({ length: 4 }, (_, i) => ({
        id: i,
        hab: 0,
        byp: 0,
        fijoOn: 1800,
        aleatorioOn: 1800,
        fijoOff: 1800,
        aleatorioOff: 3600,
        horaDesde: 1020,
        horaHasta: 420,
        descCorta: "---"
    }));
};

// --- El Componente del Formulario ---
function ConfiguracionAcForm({ config, setConfig, state, setState, originalConfigRef }) {
    const { jsonOutput, formDirty, jsonNeedsUpdate, selectedOutputId = 0 } = state;
    const theme = useTheme();
    const { currentUser, device, startLoading, endLoading } = useAppStore();
    const { dispatch } = useValue();

    const [viewState, setViewState] = useState([]);
    
    useEffect(() => {
        const hmsToComponents = (totalSeconds = 0) => ({
            h: Math.floor(totalSeconds / 3600),
            m: Math.floor((totalSeconds % 3600) / 60),
            s: totalSeconds % 60,
        });
        const hmToComponents = (totalMinutes = 0) => ({
            h: Math.floor(totalMinutes / 60),
            m: totalMinutes % 60,
        });

        if (config) {
            const newViewState = config.map(output => ({
                id: output.id,
                hab: !!output.hab,
                byp: !!output.byp,
                fijoOn: hmsToComponents(output.fijoOn),
                aleatorioOn: hmsToComponents(output.aleatorioOn),
                fijoOff: hmsToComponents(output.fijoOff),
                aleatorioOff: hmsToComponents(output.aleatorioOff),
                horaDesde: hmToComponents(output.horaDesde),
                horaHasta: hmToComponents(output.horaHasta),
                descCorta: output.descCorta,
            }));

            // CORRECCIÓN 1: Evitar actualización si el estado visual es el mismo
            if (JSON.stringify(newViewState) !== JSON.stringify(viewState)) {
                setViewState(newViewState);
            }
        }
    }, [config]);


    const isMounted = useRef(false);
    useEffect(() => {
        if (isMounted.current && viewState.length > 0) {
            const hmsToTotalSeconds = (timeObj) => (timeObj.h || 0) * 3600 + (timeObj.m || 0) * 60 + (timeObj.s || 0);
            const hmToTotalMinutes = (timeObj) => (timeObj.h || 0) * 60 + (timeObj.m || 0);

            const newConfig = viewState.map(output => ({
                id: output.id,
                hab: output.hab ? 1 : 0,
                byp: output.byp ? 1 : 0,
                fijoOn: hmsToTotalSeconds(output.fijoOn),
                aleatorioOn: hmsToTotalSeconds(output.aleatorioOn),
                fijoOff: hmsToTotalSeconds(output.fijoOff),
                aleatorioOff: hmsToTotalSeconds(output.aleatorioOff),
                horaDesde: hmToTotalMinutes(output.horaDesde),
                horaHasta: hmToTotalMinutes(output.horaHasta),
                descCorta: output.descCorta,
            }));
            // CORRECCIÓN 2: Evitar enviar actualización si la configuración es la misma
            if (JSON.stringify(newConfig) !== JSON.stringify(config)) {
                setConfig(newConfig);
            }
        } else {
            isMounted.current = true;
        }
    }, [viewState, setConfig]);

    
    useEffect(() => {
        const originalConfigString = JSON.stringify(originalConfigRef.current);
        const currentConfigString = JSON.stringify(config);
        const isDirty = originalConfigString !== currentConfigString;
        
        let needsUpdate = true;
        try {
            const jsonObject = JSON.parse(jsonOutput || '[]');
            needsUpdate = JSON.stringify(jsonObject) !== currentConfigString;
        } catch (e) { needsUpdate = true; }

        if (isDirty !== formDirty || needsUpdate !== jsonNeedsUpdate) {
            setState(prev => ({ ...prev, formDirty: isDirty, jsonNeedsUpdate: needsUpdate }));
        }
    }, [config, jsonOutput, formDirty, jsonNeedsUpdate, originalConfigRef, setState]);


    const handleOutputChange = (event) => {
        setState(prev => ({ ...prev, selectedOutputId: event.target.value }));
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        const valorFinal = type === 'checkbox' ? checked : type === 'time_object' ? value : value;
        
        const newViewState = viewState.map((output, index) => {
            if (index === selectedOutputId) {
                return { ...output, [name]: valorFinal };
            }
            return output;
        });
        setViewState(newViewState);
    };

    const handleGenerateJson = () => {
        setState(prev => ({ ...prev, jsonOutput: JSON.stringify(config, null, 2), jsonNeedsUpdate: false }));
    };

    const sendConfig = async() => {
        /*//console.log("--- Enviando Configuración GPRS JSON ---", config);
        alert("Configuración GPRS enviada. Revisa la consola.");
        originalConfigRef.current = JSON.parse(JSON.stringify(config));
        setState(prev => ({ ...prev, formDirty: false }));
        */
        //console.log("--- Enviando Configuración JSON ---", config);
        startLoading();
        setState(prev => ({ ...prev, configId: "---", estado: "Esperando respuesta del servidor/dispositivo", color: "black"}));
        const updateResult = await updateConfigAlarma(currentUser, device.devId, "CMD_SET_CONFIG_SAL_AC", config, dispatch);
        //console.log("updateResult: ", updateResult);

        if (updateResult?.deviceResponse === 0) {
            // Si el envío es exitoso, actualizamos la configuración original
            originalConfigRef.current = JSON.parse(JSON.stringify(config));
            setState(prev => ({ ...prev, formDirty: false, configId: updateResult.insertId, estado: "Última configuración actualizada con éxito.", color: "green"}));
            alert(`Configuración enviada con éxito (ID: ${updateResult.insertId})`);
        } else {
            setState(prev => ({ ...prev, configId: 0, estado: "Error. Última configuración NO actualizada.", color: "red"}));
            alert("Error al enviar la configuración.");
        }
        endLoading();
    };

    const handleCancel = () => {
        if (window.confirm("¿Seguro que quieres descartar los cambios?")) {
            setConfig(originalConfigRef.current);
        }
    };

    const currentOutputData = viewState[selectedOutputId] || {};
    
    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>Configuración de Salidas AC</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box component="form" onSubmit={(e) => { e.preventDefault(); sendConfig(); }}>
                    <Grid container spacing={4}>
                        {/* Columna izquierda: Formulario */}
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Seleccionar Salida AC</InputLabel>
                                    <Select value={selectedOutputId} label="Seleccionar Salida AC" onChange={handleOutputChange}>
                                        {viewState.map(output => <MenuItem key={output.id} value={output.id}>Salida AC {output.id}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Divider sx={{mb: 2}}><Typography variant="subtitle1">Datos para Salida AC {selectedOutputId}</Typography></Divider>
                                <FormControlLabel control={<Switch name="hab" checked={currentOutputData.hab || false} onChange={handleChange} />} label="Habilitar" />
                                
                                <Grid container spacing={2} sx={{mt: 1}}>
                                    <Grid item xs={12}><TimeInput label="Fijo Encendido" name="fijoOn" value={currentOutputData.fijoOn} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><TimeInput label="Aleatorio Encendido" name="aleatorioOn" value={currentOutputData.aleatorioOn} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><TimeInput label="Fijo Apagado" name="fijoOff" value={currentOutputData.fijoOff} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><TimeInput label="Aleatorio Apagado" name="aleatorioOff" value={currentOutputData.aleatorioOff} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><TimeInputHM label="Hora Desde" name="horaDesde" value={currentOutputData.horaDesde} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><TimeInputHM label="Hora Hasta" name="horaHasta" value={currentOutputData.horaHasta} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><FormControlLabel control={<Switch name="byp" checked={currentOutputData.byp || false} onChange={handleChange} />} label="Bypass" /></Grid>
                                </Grid>
                                <TextField fullWidth name="descCorta" label="Descripción Corta" value={currentOutputData.descCorta || ''} onChange={handleChange} sx={{mt: 2}} inputProps={{ maxLength: 15 }} />
                            </Paper>
                        </Grid>

                        {/* Columna derecha: JSON y Resumen */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            
                            {/* --- PANEL DE JSON (CÓDIGO PROPORCIONADO POR EL USUARIO) --- */}
                            <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', pt: 4 }}>
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                        position: 'absolute', top: 0, left: 12,
                                        transform: 'translateY(-50%)', bgcolor: 'background.paper',
                                        px: 1, color: 'primary.main', fontWeight: 'bold',
                                        fontSize: '0.8rem', textTransform: 'uppercase',
                                    }}
                                >
                                    JSON de Configuración
                                </Typography>
                                {jsonOutput ? (
                                    <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                                        <TextareaAutosize
                                            value={jsonOutput}
                                            minRows={10}
                                            maxRows={50}
                                            style={{
                                                width: '100%',
                                                border: `1px solid ${jsonNeedsUpdate ? theme.palette.warning.main : theme.palette.divider}`,
                                                borderRadius: '4px',
                                                padding: '8px',
                                                fontFamily: 'monospace',
                                                resize: 'vertical',
                                                backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : theme.palette.background.paper,
                                                color: theme.palette.text.primary,
                                            }}
                                            readOnly
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography color="text.secondary">
                                            Genera el JSON para previsualizarlo aquí.
                                        </Typography>
                                    </Box>
                                )}
                                <Button
                                    variant="contained"
                                    fullWidth
                                    color={jsonNeedsUpdate ? "warning" : "primary"}
                                    onClick={handleGenerateJson}
                                    sx={{ mt: 'auto' }}
                                >
                                    {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                                </Button>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>RESUMEN DE CONFIGURACIÓN</Typography>
                                <Typography variant="h6" gutterBottom>Salida AC {selectedOutputId}</Typography>
                                <Typography variant="body2">**Habilitado:** {currentOutputData.hab ? 'Sí' : 'No'}</Typography>
                                <Typography variant="body2">**Descripción:** {currentOutputData.descCorta || 'N/A'}</Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color={formDirty ? 'error' : 'inherit'}>**Formulario Modificado:** {formDirty ? 'Sí' : 'No'}</Typography>
                                    <Typography variant="body2" color={jsonNeedsUpdate ? 'warning.main' : 'inherit'}>**JSON Actualizado:** {jsonNeedsUpdate ? 'No' : 'Sí'}</Typography>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Fila inferior: Resultados del envio de la configuracion al dispositivo */}
                        <Grid item xs={12} md={12}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase'}}>
                                    Resultado envío al Dispositivo
                                </Typography>
                                <Box>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}>
                                        <strong>ConfigId: </strong>{state.configId}
                                    </Typography>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5, color: state.color }}>
                                        <strong>Estado: </strong>{state.estado}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>            
                        
                        {/* Botones de Acción */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={!formDirty}>Cancelar</Button>
                                <Button type="submit" variant="contained" disabled={jsonNeedsUpdate}>Enviar</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Container>
    );
}

export default ConfiguracionAcForm;